import React, { useState, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode } from 'primereact/api';
import { Link } from 'react-router-dom';
import GenericHistoryDialog from '../../../../../components/common/GenericHistoryDialog';
import StickyScrollTable from '../../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../../components/common/PreferencesDataTable';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';
import { useGetWarrantyClaimsHistoryQuery } from '../../../../../api';
import { getWarrantyClaimsHistoryColumns } from '../config/warrantyClaimsHistoryColumns';
import { Roles } from '../../../../../utils/roleEnums';
import { WarrantyClaimStatus } from '../../../../../utils/warrantyClaimsEnums';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { createTruncatedTextForTable } from '../../../../../utils/textUtils';

const WarrantyClaimsTable = ({
    warrantyClaims,
    loading,
    onPage,
    lazyParams,
    totalCount,
    activeIndex,
    activeFilters,
    onFiltersChange,
    dealerOptions,
    dealerFilterValue,
    setDealerFilterValue,
    setDealerSearchText,
    dealerCodeOptions,
    dealerCodeFilterValue,
    setDealerCodeFilterValue,
    setDealerCodeSearchText,
    warrantyTypeOptions,
    warrantyTypeFilterValue,
    setWarrantyTypeFilterValue,
    countryOptions,
    sortField,
    sortOrder,
    onSortChange,
    onResetFilters
}) => {
    const { th } = useLanguage();
    const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
    const [currentWarrantyClaim, setCurrentWarrantyClaim] = useState(null);
    const { hasAnyRole } = usePermissions();
    const isDealer = hasAnyRole([Roles.DEALER, Roles.SERVICE_DEALER, Roles.SALES_DEALER]);

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );
 

    // Tarih filtresi için template
    const dateFilterTemplate = (options) => {
        const handleDateChange = (e) => {
            // Tarihi normalize et: saat bilgisini 00:00:00 UTC yap
            let normalizedDate = e.value;
            if (normalizedDate instanceof Date) {
                // Yerel saat diliminde 00:00:00 olarak ayarla
                normalizedDate = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), normalizedDate.getDate());
            }
            options.filterApplyCallback(normalizedDate);
        };

        return <Calendar value={options.value} onChange={handleDateChange} dateFormat="dd.mm.yy" placeholder={th('common.search')} showIcon showButtonBar className="p-column-filter" style={{ minWidth: '12rem' }} />;
    };

    // Generic kolon config helper fonksiyonları (PreferencesDataTable icin config objesi döndürür)
    const makeTextColumn = (field, headerKey, options = {}) => {
        const { filterField = field, body, sortable = true, minWidth = '180px' } = options;

        return {
            field,
            columnKey: field,
            header: th(headerKey),
            sortable,
            style: { minWidth },
            body,
            filter: true,
            filterField,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const makeDateColumn = (field, headerKey, options = {}) => {
        const { filterField = field, sortable = true, minWidth = '180px' } = options;

        return {
            field,
            columnKey: field,
            header: th(headerKey),
            sortable,
            style: { minWidth },
            body: (row) => formatDateOnly(row?.[field]),
            filter: true,
            filterField,
            filterElement: dateFilterTemplate,
            showFilterMenu: false,
            showClearButton: false
        };
    };

    const makeDropdownColumn = (field, headerKey, filterElement, options = {}) => {
        const { filterField = field, body, sortable = true, minWidth = '180px', showFilterMenu = false } = options;

        return {
            field,
            columnKey: field,
            header: th(headerKey),
            sortable,
            style: { minWidth },
            body,
            filter: true,
            filterField,
            filterElement,
            showFilterMenu
        };
    };

    const makeCustomColumn = (options) => {
        const {
            columnKey,
            field,
            headerKey,
            body,
            sortable = true,
            minWidth = '180px',
            filter = false,
            filterField,
            filterElement,
            filterPlaceholder,
            filterMatchModeOptions,
            showFilterMenu = false,
            showClearButton = false,
            bodyClassName,
            headerClassName,
            style = {},
            exportable = true
        } = options;

        return {
            field,
            columnKey: columnKey || field,
            header: headerKey ? th(headerKey) : undefined,
            sortable,
            style: { minWidth, ...style },
            body,
            bodyClassName,
            headerClassName,
            exportable,
            filter,
            filterField,
            filterElement,
            filterPlaceholder: filterPlaceholder ? th(filterPlaceholder) : undefined,
            filterMatchModeOptions,
            showFilterMenu,
            showClearButton
        };
    };

    const statusOptions = useMemo(
        () => [
            { label: th('warrantyClaims.tabs.drafts'), value: WarrantyClaimStatus.DRAFT },
            { label: th('warrantyClaims.tabs.waiting'), value: WarrantyClaimStatus.WAITING },
            { label: th('warrantyClaims.tabs.returned'), value: WarrantyClaimStatus.RETURNED },
            { label: th('warrantyClaims.tabs.returnedToKarsan'), value: WarrantyClaimStatus.RETURNED_TO_KARSAN },
            { label: th('warrantyClaims.tabs.accepted'), value: WarrantyClaimStatus.ACCEPTED },
            { label: th('warrantyClaims.tabs.rejected'), value: WarrantyClaimStatus.REJECTED }
        ],
        [th]
    );

    const statusFilterTemplate = (options) => {
        return <MultiSelect value={options.value} options={statusOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter" maxSelectedLabels={1} style={{ minWidth: '12rem' }} />;
    };

    const approvalLabelMap = {
        mainDealer: th('warrantyClaims.approval.mainDealer'),
        warrantyTeam: th('warrantyClaims.approval.warrantyTeam')
    };

    const waitingApprovalInfoBody = (row) => {
        const v = row?.waitingApprovalInfo;
        if (!v) return '-';

        let displayValue = v;
        if (approvalLabelMap[v]) {
            displayValue = approvalLabelMap[v];
        }

        return createTruncatedTextForTable(displayValue, 20, `approver-${row.id}`);
    };

    const handleSort = (e) => {
        onSortChange?.(e);
    };

    // History dialog functions
    const showHistoryDialog = (warrantyClaim) => {
        setCurrentWarrantyClaim(warrantyClaim);
        setHistoryDialogVisible(true);
    };

    const hideHistoryDialog = () => {
        setHistoryDialogVisible(false);
        setCurrentWarrantyClaim(null);
    };

    // Use the warranty claims history query hook
    const useWarrantyClaimsHistoryQuery = useGetWarrantyClaimsHistoryQuery;

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Link to={`/warranty-management/warranty-requests/claims/${rowData.id}`} className="flex align-items-center" title={th('warrantyClaims.tooltips.edit')}>
                    <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ left: '1.5rem', color: '#0B3187' }}></i>
                </Link>
                <i onClick={() => showHistoryDialog(rowData)} className="pi pi-history  cursor-pointer text-blue-500 hover:text-blue-700" style={{ left: '1.5rem', color: '#0B3187' }} title={th('warrantyClaims.tooltips.history')}></i>
            </div>
        );
    };

    const actionFilterTemplate = () => {
        return (
            <div className="flex justify-content-center align-items-center">
                <i onClick={onResetFilters} className="pi pi-filter-slash cursor-pointer text-blue-500 hover:text-blue-700" style={{ color: '#0B3187', fontSize: '1rem' }} title={th('filter.reset')}></i>
            </div>
        );
    };

    const showWaitingApprovalColumn = !isDealer && (activeIndex === 1 || activeIndex === 3);
    const showStatusNameColumn = activeIndex === 6;
    const showSendApprovalDateColumn = activeIndex === 1 || activeIndex === 6;

    // PrimeReact kolon filtresi (row filter) -> mevcut server-side filtre akışına bağlanır
    const tableFilters = useMemo(
        () => ({
            dealerId: {
                value: activeFilters?.dealerId ?? null,
                matchMode: FilterMatchMode.IN
            },
            warrClaimsNo: {
                value: activeFilters?.warrClaimsNo ?? null,
                matchMode: activeFilters?.warrClaimsNoMatchMode ?? FilterMatchMode.CONTAINS
            },
            chassisNo: {
                value: activeFilters?.chassisNo ?? null,
                matchMode: activeFilters?.chassisNoMatchMode ?? FilterMatchMode.CONTAINS
            },
            plateNo: {
                value: activeFilters?.plateNo ?? null,
                matchMode: activeFilters?.plateNoMatchMode ?? FilterMatchMode.CONTAINS
            },
            customerName: {
                value: activeFilters?.customerName ?? null,
                matchMode: activeFilters?.customerNameMatchMode ?? FilterMatchMode.CONTAINS
            },
            CustomerStatementId: {
                value: activeFilters?.CustomerStatementId ?? null,
                matchMode: activeFilters?.CustomerStatementIdMatchMode ?? FilterMatchMode.CONTAINS
            },
            preApprovalNo: {
                value: activeFilters?.preApprovalNo ?? null,
                matchMode: activeFilters?.preApprovalNoMatchMode ?? FilterMatchMode.CONTAINS
            },
            bosBranchCode: {
                value: activeFilters?.bosBranchCode ?? null,
                matchMode: FilterMatchMode.IN
            },
            country: {
                value: activeFilters?.country ?? null,
                matchMode: FilterMatchMode.IN
            },
            warrType: {
                value: activeFilters?.warrType ?? null,
                matchMode: FilterMatchMode.EQUALS
            },
            totalPrice: {
                value: activeFilters?.totalPrice ?? null,
                matchMode: activeFilters?.totalPriceMatchMode ?? FilterMatchMode.CONTAINS
            },
            waitingApprovalInfo: {
                value: activeFilters?.waitingApprovalInfo ?? null,
                matchMode: activeFilters?.waitingApprovalInfoMatchMode ?? FilterMatchMode.CONTAINS
            },
            sbNumber: {
                value: activeFilters?.sbNumber ?? null,
                matchMode: activeFilters?.sbNumberMatchMode ?? FilterMatchMode.CONTAINS
            },
            createdName: {
                value: activeFilters?.createdName ?? null,
                matchMode: activeFilters?.createdNameMatchMode ?? FilterMatchMode.CONTAINS
            },
            createdDate: {
                value: activeFilters?.createdDate ?? null,
                matchMode: activeFilters?.createdDateMatchMode ?? FilterMatchMode.DATE_IS
            },
            claimDate: {
                value: activeFilters?.claimDate ?? null,
                matchMode: activeFilters?.claimDateMatchMode ?? FilterMatchMode.DATE_IS
            },
            repairDate: {
                value: activeFilters?.repairDate ?? null,
                matchMode: activeFilters?.repairDateMatchMode ?? FilterMatchMode.DATE_IS
            },
            warrantyStartDate: {
                value: activeFilters?.warrantyStartDate ?? null,
                matchMode: activeFilters?.warrantyStartDateMatchMode ?? FilterMatchMode.DATE_IS
            },
            standardWarrantyEndDate: {
                value: activeFilters?.standardWarrantyEndDate ?? null,
                matchMode: activeFilters?.standardWarrantyEndDateMatchMode ?? FilterMatchMode.DATE_IS
            },
            extendedWarrantyEndDate: {
                value: activeFilters?.extendedWarrantyEndDate ?? null,
                matchMode: activeFilters?.extendedWarrantyEndDateMatchMode ?? FilterMatchMode.DATE_IS
            },
            statusId: {
                value: activeFilters?.statusId ?? null,
                matchMode: FilterMatchMode.EQUALS
            },
            sendApprovalDate: {
                value: activeFilters?.sendApprovalDate ?? null,
                matchMode: activeFilters?.sendApprovalDateMatchMode ?? FilterMatchMode.DATE_IS
            },
            modifiedName: {
                value: activeFilters?.modifiedName ?? null,
                matchMode: activeFilters?.modifiedNameMatchMode ?? FilterMatchMode.CONTAINS
            },
            modifiedDate: {
                value: activeFilters?.modifiedDate ?? null,
                matchMode: activeFilters?.modifiedDateMatchMode ?? FilterMatchMode.DATE_IS
            }
        }),
        [activeFilters]
    );

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};

        const updatedFilters = {
            ...(activeFilters || {}),
            dealerId: activeFilters?.dealerId ?? null,
            warrClaimsNo: 'warrClaimsNo' in filters ? filters.warrClaimsNo?.value ?? null : activeFilters?.warrClaimsNo ?? null,
            warrClaimsNoMatchMode: 'warrClaimsNo' in filters ? filters.warrClaimsNo?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.warrClaimsNoMatchMode ?? FilterMatchMode.CONTAINS,
            chassisNo: 'chassisNo' in filters ? filters.chassisNo?.value ?? null : activeFilters?.chassisNo ?? null,
            chassisNoMatchMode: 'chassisNo' in filters ? filters.chassisNo?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.chassisNoMatchMode ?? FilterMatchMode.CONTAINS,
            plateNo: 'plateNo' in filters ? filters.plateNo?.value ?? null : activeFilters?.plateNo ?? null,
            plateNoMatchMode: 'plateNo' in filters ? filters.plateNo?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.plateNoMatchMode ?? FilterMatchMode.CONTAINS,
            customerName: 'customerName' in filters ? filters.customerName?.value ?? null : activeFilters?.customerName ?? null,
            customerNameMatchMode: 'customerName' in filters ? filters.customerName?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.customerNameMatchMode ?? FilterMatchMode.CONTAINS,
            CustomerStatementId: 'CustomerStatementId' in filters ? filters.CustomerStatementId?.value ?? null : activeFilters?.CustomerStatementId ?? null,
            CustomerStatementIdMatchMode: 'CustomerStatementId' in filters ? filters.CustomerStatementId?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.CustomerStatementIdMatchMode ?? FilterMatchMode.CONTAINS,
            preApprovalNo: 'preApprovalNo' in filters ? filters.preApprovalNo?.value ?? null : activeFilters?.preApprovalNo ?? null,
            preApprovalNoMatchMode: 'preApprovalNo' in filters ? filters.preApprovalNo?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.preApprovalNoMatchMode ?? FilterMatchMode.CONTAINS,
            bosBranchCode: activeFilters?.bosBranchCode ?? null,
            bosBranchCodeMatchMode: FilterMatchMode.CONTAINS,
            country: 'country' in filters ? (Array.isArray(filters.country?.value) && filters.country.value.length > 0 ? filters.country.value : null) : activeFilters?.country ?? null,
            countryMatchMode: FilterMatchMode.EQUALS,
            warrType: 'warrType' in filters ? filters.warrType?.value ?? null : activeFilters?.warrType ?? null,
            warrTypeMatchMode: 'warrType' in filters ? FilterMatchMode.EQUALS : FilterMatchMode.EQUALS,
            totalPrice: 'totalPrice' in filters ? filters.totalPrice?.value ?? null : activeFilters?.totalPrice ?? null,
            totalPriceMatchMode: 'totalPrice' in filters ? filters.totalPrice?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.totalPriceMatchMode ?? FilterMatchMode.CONTAINS,
            waitingApprovalInfo: 'waitingApprovalInfo' in filters ? filters.waitingApprovalInfo?.value ?? null : activeFilters?.waitingApprovalInfo ?? null,
            waitingApprovalInfoMatchMode: 'waitingApprovalInfo' in filters ? filters.waitingApprovalInfo?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.waitingApprovalInfoMatchMode ?? FilterMatchMode.CONTAINS,
            sbNumber: 'sbNumber' in filters ? filters.sbNumber?.value ?? null : activeFilters?.sbNumber ?? null,
            sbNumberMatchMode: 'sbNumber' in filters ? filters.sbNumber?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.sbNumberMatchMode ?? FilterMatchMode.CONTAINS,
            createdName: 'createdName' in filters ? filters.createdName?.value ?? null : activeFilters?.createdName ?? null,
            createdNameMatchMode: 'createdName' in filters ? filters.createdName?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.createdNameMatchMode ?? FilterMatchMode.CONTAINS,
            createdDate: 'createdDate' in filters ? filters.createdDate?.value ?? null : activeFilters?.createdDate ?? null,
            createdDateMatchMode: 'createdDate' in filters ? filters.createdDate?.matchMode ?? FilterMatchMode.DATE_IS : activeFilters?.createdDateMatchMode ?? FilterMatchMode.DATE_IS,
            claimDate: 'claimDate' in filters ? filters.claimDate?.value ?? null : activeFilters?.claimDate ?? null,
            claimDateMatchMode: 'claimDate' in filters ? filters.claimDate?.matchMode ?? FilterMatchMode.DATE_IS : activeFilters?.claimDateMatchMode ?? FilterMatchMode.DATE_IS,
            repairDate: 'repairDate' in filters ? filters.repairDate?.value ?? null : activeFilters?.repairDate ?? null,
            repairDateMatchMode: 'repairDate' in filters ? filters.repairDate?.matchMode ?? FilterMatchMode.DATE_IS : activeFilters?.repairDateMatchMode ?? FilterMatchMode.DATE_IS,
            warrantyStartDate: 'warrantyStartDate' in filters ? filters.warrantyStartDate?.value ?? null : activeFilters?.warrantyStartDate ?? null,
            warrantyStartDateMatchMode: 'warrantyStartDate' in filters ? filters.warrantyStartDate?.matchMode ?? FilterMatchMode.DATE_IS : activeFilters?.warrantyStartDateMatchMode ?? FilterMatchMode.DATE_IS,
            standardWarrantyEndDate: 'standardWarrantyEndDate' in filters ? filters.standardWarrantyEndDate?.value ?? null : activeFilters?.standardWarrantyEndDate ?? null,
            standardWarrantyEndDateMatchMode: 'standardWarrantyEndDate' in filters ? filters.standardWarrantyEndDate?.matchMode ?? FilterMatchMode.DATE_IS : activeFilters?.standardWarrantyEndDateMatchMode ?? FilterMatchMode.DATE_IS,
            extendedWarrantyEndDate: 'extendedWarrantyEndDate' in filters ? filters.extendedWarrantyEndDate?.value ?? null : activeFilters?.extendedWarrantyEndDate ?? null,
            extendedWarrantyEndDateMatchMode: 'extendedWarrantyEndDate' in filters ? filters.extendedWarrantyEndDate?.matchMode ?? FilterMatchMode.DATE_IS : activeFilters?.extendedWarrantyEndDateMatchMode ?? FilterMatchMode.DATE_IS,
            statusId: 'statusId' in filters ? filters.statusId?.value ?? null : activeFilters?.statusId ?? null,
            sendApprovalDate: 'sendApprovalDate' in filters ? filters.sendApprovalDate?.value ?? null : activeFilters?.sendApprovalDate ?? null,
            sendApprovalDateMatchMode: 'sendApprovalDate' in filters ? filters.sendApprovalDate?.matchMode ?? FilterMatchMode.DATE_IS : activeFilters?.sendApprovalDateMatchMode ?? FilterMatchMode.DATE_IS,
            modifiedName: 'modifiedName' in filters ? filters.modifiedName?.value ?? null : activeFilters?.modifiedName ?? null,
            modifiedNameMatchMode: 'modifiedName' in filters ? filters.modifiedName?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.modifiedNameMatchMode ?? FilterMatchMode.CONTAINS,
            modifiedDate: 'modifiedDate' in filters ? filters.modifiedDate?.value ?? null : activeFilters?.modifiedDate ?? null,
            modifiedDateMatchMode: 'modifiedDate' in filters ? filters.modifiedDate?.matchMode ?? FilterMatchMode.DATE_IS : activeFilters?.modifiedDateMatchMode ?? FilterMatchMode.DATE_IS
        };
        onFiltersChange?.(updatedFilters);
    };

    // Dealer multi-select filter element
    const dealerFilterTemplate = () => {
        return (
            <MultiSelect
                value={activeFilters?.dealerId ?? null}
                options={dealerOptions || []}
                onChange={(e) => {
                    const selected = e.value;
                    onFiltersChange({
                        ...activeFilters,
                        dealerId: Array.isArray(selected) && selected.length > 0 ? selected : null
                    });
                }}
                onHide={() => {
                    setDealerFilterValue('');
                    setDealerSearchText('');
                }}
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                filter
                resetFilterOnHide
                filterBy="label"
                filterPlaceholder={th('common.search')}
                filterValue={dealerFilterValue}
                onFilter={(e) => {
                    setDealerFilterValue(e.filter);
                    setDealerSearchText(e.filter);
                }}
                maxSelectedLabels={1}
                selectedItemsLabel="{0} seçildi"
                style={{ minWidth: '12rem' }}
            />
        );
    };

    const dealerCodeFilterTemplate = () => {
        return (
            <MultiSelect
                value={activeFilters?.bosBranchCode ?? null}
                options={dealerCodeOptions || []}
                onChange={(e) => {
                    const selected = e.value;
                    onFiltersChange({
                        ...activeFilters,
                        bosBranchCode: Array.isArray(selected) && selected.length > 0 ? selected : null,
                        bosBranchCodeMatchMode: FilterMatchMode.CONTAINS
                    });
                }}
                onHide={() => {
                    setDealerCodeFilterValue('');
                    setDealerCodeSearchText('');
                }}
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                filter
                resetFilterOnHide
                filterBy="label"
                filterPlaceholder={th('common.search')}
                filterValue={dealerCodeFilterValue}
                onFilter={(e) => {
                    setDealerCodeFilterValue(e.filter);
                    setDealerCodeSearchText(e.filter);
                }}
                maxSelectedLabels={1}
                selectedItemsLabel="{0} seçildi"
                style={{ minWidth: '12rem' }}
            />
        );
    };

    const [countryFilterValue, setCountryFilterValue] = useState('');

    const countryFilterTemplate = () => {
        return (
            <MultiSelect
                value={activeFilters?.country ?? null}
                options={countryOptions || []}
                onChange={(e) => {
                    const selected = e.value;
                    onFiltersChange({
                        ...activeFilters,
                        country: Array.isArray(selected) && selected.length > 0 ? selected : null,
                        countryMatchMode: FilterMatchMode.EQUALS
                    });
                }}
                onHide={() => setCountryFilterValue('')}
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                filter
                resetFilterOnHide
                filterBy="label"
                filterValue={countryFilterValue}
                onFilter={(e) => setCountryFilterValue(e.filter)}
                maxSelectedLabels={1}
                selectedItemsLabel="{0} seçildi"
                style={{ minWidth: '12rem' }}
            />
        );
    };

    // Warranty Type dropdown filter element
    const warrantyTypeFilterTemplate = (options) => {
        return (
            <Dropdown
                value={options.value}
                options={warrantyTypeOptions || []}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                filter
                filterBy="label"
                filterPlaceholder={th('common.search')}
                filterValue={warrantyTypeFilterValue}
                onFilter={(e) => {
                    setWarrantyTypeFilterValue(e.filter);
                }}
                style={{ minWidth: '12rem' }}
            />
        );
    };

    // Kolon config dizisi (PreferencesDataTable -> useTablePreferences ile siralama/gizleme yonetilir).
    // Kosullu kolonlar (tab/role) sadece gecerli oldugunda eklenir; hook reconciliation gormedigi
    // kolonu yok sayar, geldiginde kayitli sirasinda gosterir.
    const columns = [
        {
            field: '__actions',
            columnKey: 'action',
            lockedFromPrefs: true,
            lockedPosition: 'start',
            body: actionBodyTemplate,
            bodyClassName: 'text-center',
            headerClassName: 'text-center',
            exportable: false,
            style: { width: '5%', minWidth: '33px' },
            sortable: false,
            filter: true,
            filterElement: actionFilterTemplate,
            showFilterMenu: false,
            showClearButton: false
        },
        ...(showStatusNameColumn ? [makeDropdownColumn('statusName', 'warrantyClaims.table.statusName', statusFilterTemplate, { filterField: 'statusId', showFilterMenu: false })] : []),
        ...(!isDealer ? [makeDropdownColumn('bosBranchCode', 'customer.dealerCode', dealerCodeFilterTemplate, { showFilterMenu: false })] : []),
        ...(!isDealer ? [makeDropdownColumn('bosBranchName', 'common.dealer', dealerFilterTemplate, { filterField: 'dealerId', body: (rowData) => createTruncatedTextForTable(rowData.bosBranchName, 40, `dealer-${rowData.id}`) })] : []),
        ...(!isDealer ? [makeDropdownColumn('country', 'warrantyClaims.table.country', countryFilterTemplate, { minWidth: '130px', showFilterMenu: false })] : []),
        makeTextColumn('warrClaimsNo', 'warrantyClaims.table.warrClaimsNo'),
        makeTextColumn('plateNo', 'warranty.customerDeclaration.table.plateNo'),
        makeTextColumn('chassisNo', 'warrantyClaims.table.chassisNo'),
        makeTextColumn('customerName', 'warranty.customerDeclaration.table.customer', { body: (rowData) => createTruncatedTextForTable(rowData.customerName, 30, `customer-${rowData.id}`) }),
        makeTextColumn('workOrder', 'warrantyClaims.table.workOrder', { filterField: 'CustomerStatementId' }),
        makeDropdownColumn('warrType', 'warrantyClaims.table.warrantyType', warrantyTypeFilterTemplate, { minWidth: '120px' }),
        makeTextColumn('totalPrice', 'warrantyClaims.table.totalPrice'),
        ...(showWaitingApprovalColumn
            ? [
                  makeCustomColumn({
                      columnKey: 'waitingApprovalInfo',
                      field: 'waitingApprovalInfo',
                      headerKey: 'warrantyClaims.table.currentApproverNames',
                      body: waitingApprovalInfoBody,
                      filter: true,
                      filterField: 'waitingApprovalInfo',
                      filterPlaceholder: 'common.search',
                      filterMatchModeOptions: textMatchModeOptions,
                      showFilterMenu: true,
                      showClearButton: false,
                      sortable: true
                  })
              ]
            : []),
        makeTextColumn('preApprovalNo', 'warrantyClaims.table.preApprovalNo'),
        makeTextColumn('sbNumber', 'warrantyClaims.table.sbNumber'),
        makeCustomColumn({
            columnKey: 'createdName',
            field: 'createdName',
            headerKey: 'warrantyClaims.table.createdName',
            body: (rowData) => createTruncatedTextForTable(rowData.createdName, 40, `createdName-${rowData.id}`),
            filter: true,
            filterField: 'createdName',
            filterPlaceholder: 'common.search',
            filterMatchModeOptions: textMatchModeOptions,
            showFilterMenu: true,
            showClearButton: false
        }),
        makeCustomColumn({
            columnKey: 'modifiedName',
            field: 'modifiedName',
            headerKey: 'warrantyClaims.table.modifiedName',
            body: (rowData) => createTruncatedTextForTable(rowData.modifiedName, 40, `modifiedName-${rowData.id}`),
            filter: true,
            filterField: 'modifiedName',
            filterPlaceholder: 'common.search',
            filterMatchModeOptions: textMatchModeOptions,
            showFilterMenu: true,
            showClearButton: false
        }),
        makeDateColumn('createdDate', 'warrantyClaims.table.createdDate'),
        makeDateColumn('modifiedDate', 'warrantyClaims.table.modifiedDate'),
        ...(showSendApprovalDateColumn ? [makeDateColumn('sendApprovalDate', 'warrantyClaims.table.sendApprovalDate')] : []),
        makeDateColumn('claimDate', 'warrantyClaims.table.failureDate'),
        makeDateColumn('repairDate', 'warrantyClaims.table.repairDate'),
        makeDateColumn('warrantyStartDate', 'warrantyClaims.table.warrantyStartDate'),
        makeDateColumn('standardWarrantyEndDate', 'warrantyClaims.table.standardWarrantyEndDate'),
        makeDateColumn('extendedWarrantyEndDate', 'warrantyClaims.table.extendedWarrantyEndDate')
    ];

    return (
        <>
            <GenericHistoryDialog
                hideDialog={hideHistoryDialog}
                dialogVisible={historyDialogVisible}
                entity={currentWarrantyClaim}
                entityType="warrantyClaim"
                useHistoryQuery={useWarrantyClaimsHistoryQuery}
                columns={getWarrantyClaimsHistoryColumns(th)}
                titleKey="warrantyClaims.history.title"
                entityIdentifierField="warrClaimsNo"
            />

            <StickyScrollTable deps={[warrantyClaims, activeIndex]}>
            <PreferencesDataTable
                tableKey="warranty-claims"
                columns={columns}
                value={warrantyClaims}
                totalRecords={totalCount}
                className="p-datatable-gridlines"
                showGridlines
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows || 10}
                onPage={onPage}
                dataKey="id"
                loading={loading}
                filterDisplay="row"
                filters={tableFilters}
                onFilter={handleTableFilter}
                emptyMessage={th('warrantyClaims.table.noRecordsFound')}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                tableStyle={{ minWidth: '60rem' }}
                onSort={handleSort}
                sortField={sortField}
                sortOrder={sortOrder}
                sortMode="single"
                pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
            />
            </StickyScrollTable>
        </>
    );
};

export default WarrantyClaimsTable;
